/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormSection } from '@/shared/ui/components/form';
import { SearchAutocomplete, type SearchAutocompleteOption } from '@/shared/ui/components/form/SearchAutocomplete';
import { Text, Textarea } from '@/shared/ui';
import { FORM_FIELD_LIMITS } from '@/shared/utils/form-field-limits';
import { cn } from '@/shared/ui/utils/tailwind';
import { getMissingFieldLabels } from './missing-field-labels';
import { VisitPreviewCard } from './VisitPreviewCard';
import { PropertyClientSection } from './PropertyClientSection';
import { DateTimeSection } from './DateTimeSection';
import { labelStyle, textareaStyle, errorStyle } from './drawer-styles';
import type { VisitFormData } from './schema';

export type VisitDrawerFieldsProps = {
  form: UseFormReturn<VisitFormData>;
  agencyId: string;
  propertyOptions: SearchAutocompleteOption[];
  clientOptions: SearchAutocompleteOption[];
  brokerOptions: SearchAutocompleteOption[];
  /** Hides the broker select for solo brokers, who are always the broker. */
  showBrokerField: boolean;
  occupiedSlots: Set<string>;
  selectedProperty: SearchAutocompleteOption | null;
  selectedClient: SearchAutocompleteOption | null;
  visitDate: string;
  visitTime: string;
  hasValidationErrors: boolean;
};

/**
 * Full form body for the visit drawer: property/client, date/time, broker,
 * notes, a live preview card, and a validation alert when fields are missing.
 *
 * @param props - The form instance, option lists, occupied slots, selection, and validation state.
 * @returns The visit drawer form body.
 */
export function VisitDrawerFields({
  form,
  agencyId,
  propertyOptions,
  clientOptions,
  brokerOptions,
  showBrokerField,
  occupiedSlots,
  selectedProperty,
  selectedClient,
  visitDate,
  visitTime,
  hasValidationErrors,
}: VisitDrawerFieldsProps) {
  const { t } = useTranslation('features/visits');
  const { errors } = form.formState;

  const scrollAlertIntoView = (node: HTMLDivElement | null) => {
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="flex flex-col gap-(--space-3)">
      <FormSection title={t('drawer.propertyAndClient')} variant="quiet">
        <PropertyClientSection
          control={form.control}
          agencyId={agencyId}
          clientId={form.watch('clientId')}
          propertyOptions={propertyOptions}
          clientOptions={clientOptions}
        />
      </FormSection>

      <FormSection title={t('drawer.dateAndTime')} variant="quiet">
        <DateTimeSection control={form.control} occupiedSlots={occupiedSlots} />
      </FormSection>

      {showBrokerField && (
        <FormSection title={t('drawer.broker')} variant="quiet">
          <Controller
            name="brokerEmail"
            control={form.control}
            render={({ field }) => (
              <SearchAutocomplete
                placeholder={t('drawer.selectBroker')}
                options={brokerOptions}
                value={field.value || null}
                onChange={(val) => field.onChange(val ?? '')}
              />
            )}
          />
        </FormSection>
      )}

      <div className="flex flex-col">
        <Text as="label" style={labelStyle}>{t('drawer.notes')}</Text>
        <Textarea
          {...form.register('notes')}
          placeholder={t('drawer.notesPlaceholder')}
          rows={3}
          maxLength={FORM_FIELD_LIMITS.notes}
          style={textareaStyle}
        />
      </div>

      <VisitPreviewCard
        property={selectedProperty}
        client={selectedClient}
        visitDate={visitDate}
        visitTime={visitTime}
      />

      {hasValidationErrors && (
        <ValidationAlert
          errors={errors as Record<string, unknown>}
          scrollRef={scrollAlertIntoView}
          t={t}
        />
      )}
    </div>
  );
}

type ValidationAlertProps = {
  errors: Record<string, unknown>;
  scrollRef: (node: HTMLDivElement | null) => void;
  t: (key: string, opts?: Record<string, string>) => string;
};

/**
 * Alert banner listing the required fields that are still missing. Scrolls
 * itself into view when mounted so the user sees the error immediately.
 *
 * @param props - The form errors, a scroll-into-view ref callback, and the translator.
 * @returns The validation alert banner.
 */
function ValidationAlert({ errors, scrollRef, t }: ValidationAlertProps) {
  const missing = getMissingFieldLabels(errors);
  return (
    <div
      ref={scrollRef}
      role="alert"
      className={cn(
        'rounded-(--radius-md) border px-(--space-3) py-(--space-3)',
        'border-[color-mix(in_srgb,var(--status-error)_20%,transparent)]',
        'bg-[color-mix(in_srgb,var(--status-error)_8%,transparent)]',
      )}
      style={errorStyle}
    >
      {missing.length > 0
        ? t('drawer.fillRequiredFieldsWith', { fields: missing.join(', ') })
        : t('drawer.fillRequiredFields')}
    </div>
  );
}
